"use client";

import { Spinner } from "@/components/ui/spinner";

import { useEffect, useLayoutEffect, useRef, useSyncExternalStore } from "react";
import { useAuthSession } from "@/lib/auth/auth-provider";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { CamundaSidebar } from "@/components/camunda-sidebar";
import {
  clearAuthState,
  getAuthStateSnapshot,
  subscribeAuthState,
} from "@/lib/auth-store";
import {
  AUTH_LOGOUT_INTENT_STORAGE_KEY,
  clearAuthRedirectLock,
  clearLogoutIntent,
  hasLogoutIntent,
  syncAuthStateFromSession,
  syncAuthStateWithSession,
} from "@/lib/auth-client";
import { CAMUNDA_AUTH_EXPIRED_EVENT } from "@/lib/camunda/base-service";
import { canRenderProtectedTaskArea } from "./task-area-auth";

const subscribeToHydration = () => () => {};
const getHydratedSnapshot = () => true;
const getServerHydrationSnapshot = () => false;

interface CamundaAuthExpiredDetail {
  reason?: string;
  sessionRecoveryExhausted?: boolean;
}

function isCamundaAuthExpiredEvent(
  event: Event,
): event is CustomEvent<CamundaAuthExpiredDetail> {
  return event instanceof CustomEvent;
}

export default function TaskListClientLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { status, session, login } = useAuthSession();
  const accessToken = session?.accessToken;
  const preferredUsername = session?.preferredUsername;
  const idToken = session?.idToken;
  const expiresAt = session?.expiresAt;
  const authRedirectInProgressRef = useRef(false);
  const authState = useSyncExternalStore(
    subscribeAuthState,
    getAuthStateSnapshot,
    getAuthStateSnapshot,
  );
  const hasMounted = useSyncExternalStore(
    subscribeToHydration,
    getHydratedSnapshot,
    getServerHydrationSnapshot,
  );
  const protectedContentReady = canRenderProtectedTaskArea(
    hasMounted,
    status,
    authState.accessToken,
  );

  const redirectToLoginRef = useRef<(
    reason: string,
    forceFreshLogin?: boolean,
  ) => void>(() => {});

  useLayoutEffect(() => {
    redirectToLoginRef.current = (reason: string, forceFreshLogin = false) => {
      if (authRedirectInProgressRef.current) {
        return;
      }

      authRedirectInProgressRef.current = true;
      console.warn(`[AUTH] Redirigiendo a login: ${reason}`);
      clearAuthState();
      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (forceFreshLogin) {
        void login(currentPath, true).catch(() => {
          authRedirectInProgressRef.current = false;
          console.error("[AUTH] No se pudo iniciar la reautenticacion forzada");
        });
        return;
      }

      const nextUrl =
        currentPath.startsWith("/") && currentPath !== "/"
          ? `/?callbackUrl=${encodeURIComponent(currentPath)}`
          : "/";
      window.location.assign(nextUrl);
    };
  }, [login]);

  useEffect(() => {
    if (status === "authenticated" && accessToken) {
      authRedirectInProgressRef.current = false;
      clearAuthRedirectLock();
      clearLogoutIntent();
      syncAuthStateWithSession({
        accessToken,
        preferredUsername,
        idToken,
        expiresAt,
      });
    } else if (status === "unauthenticated") {
      clearAuthState();
    }
  }, [status, accessToken, preferredUsername, idToken, expiresAt]);

  useEffect(() => {
    if (status === "unauthenticated" && hasLogoutIntent()) {
      return;
    }

    if (status === "unauthenticated") {
      redirectToLoginRef.current("unauthenticated");
    }

  }, [status]);

  useEffect(() => {
    let isCancelled = false;

    const handler = async (event: Event) => {
      if (!isCamundaAuthExpiredEvent(event)) {
        return;
      }
      const detail = event.detail;
      if (detail?.sessionRecoveryExhausted) {
        redirectToLoginRef.current(
          detail.reason ?? "session_recovery_exhausted",
          true,
        );
        return;
      }

      const sessionRecovered = await syncAuthStateFromSession();
      if (sessionRecovered || isCancelled) {
        return;
      }

      redirectToLoginRef.current(detail?.reason ?? "camunda_auth_expired");
    };

    window.addEventListener(CAMUNDA_AUTH_EXPIRED_EVENT, handler);
    return () => {
      isCancelled = true;
      window.removeEventListener(CAMUNDA_AUTH_EXPIRED_EVENT, handler);
    };
  }, []);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== AUTH_LOGOUT_INTENT_STORAGE_KEY || event.newValue !== "1") {
        return;
      }

      clearAuthState();
      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const nextUrl =
        currentPath.startsWith("/") && currentPath !== "/"
          ? `/?loggedOut=1&callbackUrl=${encodeURIComponent(currentPath)}`
          : "/?loggedOut=1";
      window.location.assign(nextUrl);
    };

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  if (!protectedContentReady) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-white">
        <div className="text-center">
          <Spinner size={64} className="text-neutral-900 mx-auto mb-4" />
          <p className="text-gray-600">Verificando acceso...</p>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <CamundaSidebar />
      <SidebarTrigger
        aria-label="Abrir menu"
        className="fixed left-3 top-3 z-50 size-9 border border-gray-200 bg-white shadow-sm lg:hidden"
      />
      <div className="flex h-screen w-full min-w-0">
        <main className="min-w-0 flex-1 overflow-auto">
          <div className="h-full min-w-0">{children}</div>
        </main>
      </div>
    </SidebarProvider>
  );
}
